/* eslint-disable @typescript-eslint/dot-notation */
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TokenClaims } from '@azure/msal-common';
import {
  OrganisationClient,
  OrganisationViewModel,
  RoleClient,
  RoleValidityRequest,
  RoleViewModel,
  UserAdministrationClient,
  UserAdministrationIncidentRequest,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { ContactSearchFormlyFieldService } from '@core/services/contact-search-formly-field-service/contact-search-formly-field.service';
import { EmployeeSearchFormlyFieldService } from '@core/services/employee-search-formly-field-service/employee-search-formly-field.service';
import { OrganisationSearchFormlyFieldService } from '@core/services/organisation-search-formly-field-service/organisation-search-formly-field.service';
import { UserService } from '@core/services/oauth-service/user.service';
import { TicketCreateBaseComponent } from '@features/tickets/components/ticket-create/ticket-create.base';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { RegexpEmail } from '@shared/utils/regex';
import moment from 'moment';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';

@Component({
  selector: 'app-user-create',
  templateUrl: './user-create.component.html',
  styleUrls: ['./user-create.component.scss'],
  standalone: false,
})
export class UserCreateComponent
  extends TicketCreateBaseComponent
  implements OnInit
{
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly employeeFormlyFieldService = inject(
    EmployeeSearchFormlyFieldService
  );
  private readonly contactFormlyFieldService = inject(
    ContactSearchFormlyFieldService
  );
  private readonly organisationFormlyFieldService = inject(
    OrganisationSearchFormlyFieldService
  );
  private readonly roleClient = inject(RoleClient);
  private readonly userService = inject(UserService);
  private readonly organisationClient = inject(OrganisationClient);
  private readonly userAdministrationClient = inject(UserAdministrationClient);
  private readonly route = inject(ActivatedRoute);

  headline = this.translateService.instant(
    'menu.user_administration.component.user_creation'
  );

  cardUser: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant('user.request.create.title'),
      },
    },
    footer: {
      button1: {
        label: this.translateService.instant(
          'user.request.create.label__submit'
        ),
        onClick: () => this.validateForm(),
        disabled: false,
      },
      button2: {
        label: this.translateService.instant(
          'user.request.create.label__reset'
        ),
        onClick: () => this.reset(),
        disabled: false,
      },
    },
  };

  isTelekomChecked = false;
  isRoleOplExternal = false;
  organisationIdParam: string | null = null;
  rolesLoading = true;

  selectedRolesValidFrom: Date | undefined;
  selectedRolesValidUntil: Date | undefined;

  isUserSelected = false;

  roles$ = this.roleClient.get().pipe(shareReplay(1));

  organisations$!: Observable<OrganisationViewModel[]>;

  isTelekomUser = false;

  currentUserTokenClaims: TokenClaims & {
    [key: string]: unknown;
  };

  override successMessage = this.translateService.instant(
    'administration.create_user.success_message'
  );

  ngOnInit(): void {
    this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);

    this.currentUserTokenClaims = this.userService.getCurrentUserTokenClaims();

    this.isTelekomUser =
      this.currentUserTokenClaims.ciamId &&
      this.currentUserTokenClaims.ciamId !== '';

    const externalParam = this.route.snapshot.queryParamMap.get('external');
    const roleParam = this.route.snapshot.queryParamMap.get('role');
    this.organisationIdParam =
      this.route.snapshot.queryParamMap.get('organisationId');
    this.organisations$ = this.organisationClient
      .getAdminPortalRelevantOrSupplier()
      .pipe(
        switchMap(organisations =>
          this.withOrganisationIdOption(organisations)
        ),
        shareReplay(1)
      );
    const hasExternalParam = externalParam !== null;
    const isExternalFromQuery =
      externalParam?.toLowerCase() === 'true' || externalParam === '1';
    this.isRoleOplExternal = roleParam?.toLowerCase() === 'opl_external';

    this.isTelekomChecked = hasExternalParam
      ? !isExternalFromQuery
      : this.isRoleOplExternal
        ? false
        : !!this.isTelekomUser;

    this.rolesLoading = true;
    this.loadingMessage = this.translateService.instant('form.loading');
    this.roles$
      .pipe(
        take(1),
        finalize(() => {
          this.formFields =
            this.isTelekomChecked && this.isTelekomUser
              ? this.getFormFieldsForTelekom()
              : this.getFormFieldsForExternal();
          this.loadingMessage = this.translateService.instant(
            'form.loading_message'
          );
          this.rolesLoading = false;
        })
      )
      .subscribe();
  }

  reset() {
    this.router.navigateByUrl('dashboard');
  }

  private withOrganisationIdOption(
    organisations: OrganisationViewModel[] | null
  ) {
    const currentOrganisations = organisations;
    if (
      !this.organisationIdParam ||
      currentOrganisations.some(
        organisation => organisation.id === this.organisationIdParam
      )
    ) {
      const completeOrganisations =
        this.deduplicateOrganisations(currentOrganisations);
      return of(completeOrganisations);
    }

    return this.organisationClient.getById(this.organisationIdParam).pipe(
      map((organisationById: OrganisationViewModel | null) => {
        const mergedOrganisations = [
          ...currentOrganisations,
          ...[organisationById],
        ];

        const completeOrganisations =
          this.deduplicateOrganisations(mergedOrganisations);
        return completeOrganisations;
      }),
      catchError(() => {
        const completeOrganisations =
          this.deduplicateOrganisations(currentOrganisations);
        return of(completeOrganisations);
      })
    );
  }

  private deduplicateOrganisations(
    organisations: OrganisationViewModel[]
  ): OrganisationViewModel[] {
    const seenIds = new Set<string>();

    return organisations.filter(organisation => {
      if (!organisation.id) {
        return true;
      }

      if (seenIds.has(organisation.id)) {
        return false;
      }

      seenIds.add(organisation.id);
      return true;
    });
  }

  toggleTelekomCheck() {
    if (this.isTelekomChecked) {
      this.formFields = this.getFormFieldsForTelekom();
      this.form.get('acceptTerms')?.disable();
    } else {
      this.formFields = this.getFormFieldsForExternal();
      this.form.get('acceptTerms')?.enable();
    }

    this.form.patchValue(
      {
        isTelekomChecked: this.isTelekomChecked,
      },
      { emitEvent: false }
    );
  }

  getFormFieldsForTelekom() {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'isTelekomChecked',
            type: 'select',
            defaultValue: this.isTelekomChecked,
            props: {
              options: [
                {
                  id: 1,
                  label: this.translateService.instant(
                    'administration.create_user.telekom_employee'
                  ),
                  value: true,
                },
                {
                  id: 2,
                  label: this.translateService.instant(
                    'administration.create_user.external_user'
                  ),
                  value: false,
                },
              ],
            },
            hooks: {
              onInit: field => {
                field.formControl.valueChanges.subscribe(value => {
                  this.isTelekomChecked = value;

                  field.formControl.disable({ emitEvent: false });
                  this.toggleTelekomCheck();
                  field.formControl.enable({ emitEvent: false });
                });
              },
            },
          },
          {
            wrappers: [],
            fieldGroup: [
              {
                type: 'chapter',
                props: {
                  chapterTitle: this.translateService.instant(
                    'user.request.create.label__searchPerson'
                  ),
                },
              },
              {
                key: 'employee',
                fieldGroup: [
                  ...this.employeeFormlyFieldService.autocompleteSearch(
                    this.translateService.instant(
                      'user.request.create.placeholder__search'
                    ),
                    this.onSelectUser.bind(this),
                    false,
                    true,
                    null,
                    true,
                    this.onDelete.bind(this)
                  ),
                ],
              },
            ],
          },
          {
            type: 'flex-layout',
            key: 'userData',
            fieldGroup: [
              {
                key: 'firstName',
                type: 'input',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__firstName'
                  ),
                  required: true,
                  disabled: true,
                },
              },
              {
                key: 'lastName',
                type: 'input',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__lastName'
                  ),
                  required: true,
                  disabled: true,
                },
              },
              {
                key: 'email',
                type: 'input',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__email'
                  ),
                  required: true,
                  disabled: true,
                },
              },
              {
                key: 'roles',
                type: 'autocomplete-chips-for-roles',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__roles'
                  ),
                  required: true,
                  options: this.roles$.pipe(
                    map(roles =>
                      roles
                        .filter(
                          role =>
                            role.name.startsWith('REM_') ||
                            role.name.startsWith('DTAG_') ||
                            role.name.startsWith('IT_')
                        )
                        .sort((a, b) =>
                          a.description.localeCompare(b.description)
                        )
                    )
                  ),
                },
                validation: {
                  messages: {
                    required: this.translateService.instant(
                      'formErrors.required',
                      {
                        label: this.translateService.instant(
                          'user.request.create.label__roles'
                        ),
                      }
                    ),
                  },
                },
              },
            ],
          },
        ],
      },
    ];
  }

  getFormFieldsForExternal() {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'isTelekomChecked',
            type: 'select',
            defaultValue:
              this.isTelekomChecked && this.isTelekomUser ? true : false,
            props: {
              disabled: !this.isTelekomUser,
              options: [
                {
                  id: 1,
                  label: this.translateService.instant(
                    'administration.create_user.telekom_employee'
                  ),
                  value: true,
                },
                {
                  id: 2,
                  label: this.translateService.instant(
                    'administration.create_user.external_user'
                  ),
                  value: false,
                },
              ],
            },
            hooks: {
              onInit: field => {
                field.formControl.valueChanges.subscribe(value => {
                  this.isTelekomChecked = value;

                  field.formControl.disable({ emitEvent: false });
                  this.toggleTelekomCheck();
                  field.formControl.enable({ emitEvent: false });
                });
              },
            },
          },
          {
            wrappers: [],
            fieldGroup: [
              {
                type: 'chapter',
                props: {
                  chapterTitle: this.translateService.instant(
                    'user.request.create.label__searchContact'
                  ),
                },
              },
              {
                key: 'contact',
                fieldGroup: [
                  ...this.contactFormlyFieldService.autocompleteSearch(
                    this.translateService.instant(
                      'user.request.create.placeholder__search'
                    ),
                    this.onSelectUser.bind(this),
                    false,
                    true,
                    null,
                    true,
                    this.onDelete.bind(this)
                  ),
                ],
              },
            ],
          },
          {
            type: 'flex-layout',
            key: 'userData',
            fieldGroup: [
              {
                key: 'firstName',
                type: 'input',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__firstName'
                  ),
                  required: true,
                },
              },
              {
                key: 'lastName',
                type: 'input',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__lastName'
                  ),
                  required: true,
                },
              },
              {
                key: 'email',
                type: 'input',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__email'
                  ),
                  required: true,
                  pattern: RegexpEmail,
                },
                validation: {
                  messages: {
                    pattern: (_err, _field: FormlyFieldConfig) =>
                      this.translateService.instant('formErrors.email.pattern'),
                  },
                },
              },
              {
                key: 'organisation',
                fieldGroup: [
                  ...this.organisationFormlyFieldService.autocompleteSearch(
                    this.translateService.instant(
                      'user.request.create.label__organisation'
                    ),
                    this.organisations$,
                    undefined,
                    true,
                    false,
                    true,
                    undefined,
                    this.organisationIdParam
                  ),
                ],
              },
              {
                key: 'roles',
                type: 'button-select-roles',
                props: {
                  label: this.translateService.instant(
                    'user.request.create.label__selectRoles'
                  ),
                  rolesLabel: this.translateService.instant(
                    'user.request.create.label__roles'
                  ),
                  dialogTitle: 'user.request.create.dialog.title',
                  required: true,
                  options: this.roles$.pipe(
                    map(roles =>
                      roles
                        .filter(role => role.name.startsWith('DL_'))
                        .sort((a, b) =>
                          a.description.localeCompare(b.description)
                        )
                    )
                  ),
                  onSelectionChange: result =>
                    this.onRolesSelectionChange(result),
                },
                hooks: {
                  onInit: field => {
                    if (!this.isRoleOplExternal) {
                      return;
                    }

                    this.roles$.pipe(take(1)).subscribe(roles => {
                      const oplExtRole = roles.find(
                        role => role.name === 'DL_OPL_EXTERN'
                      );
                      if (!oplExtRole) {
                        return;
                      }

                      const currentValue = field.formControl.value;
                      if (Array.isArray(currentValue) && currentValue?.length) {
                        return;
                      }

                      field.formControl.setValue([oplExtRole]);
                    });
                  },
                },
                validation: {
                  messages: {
                    required: this.translateService.instant(
                      'formErrors.required',
                      {
                        label: this.translateService.instant(
                          'user.request.create.label__roles'
                        ),
                      }
                    ),
                  },
                },
              },
            ],
          },
          {
            key: 'acceptTerms',
            type: 'checkbox',
            wrappers: ['no-border'],
            defaultValue: false,
            props: {
              label: this.translateService.instant(
                'register.data_privacy_accept'
              ),
              appearance: 'outline',
            },
            validators: {
              validation: ['requiredTrue'],
            },
            validation: {
              messages: {
                required: this.translateService.instant(
                  'register.data_privacy_error_message'
                ),
              },
            },
          },
          {
            key: 'link',
            type: 'link',
            className: 'form-link',
            props: {
              label: this.translateService.instant(
                'register.data_privacy_link'
              ),
              href: '/privacy',
              icon: 'file-lines',
            },
          },
        ],
      },
    ];
  }

  onSelectUser(_id: string, _initialSearch, user) {
    this.formFields[0].form
      ?.get('userData.firstName')
      ?.setValue(user.firstName);
    this.formFields[0].form?.get('userData.lastName')?.setValue(user.lastName);
    this.formFields[0].form?.get('userData.email')?.setValue(user.email);
    this.formFields[0].form
      ?.get('userData.organisation.searchResult')
      ?.setValue(user.organisation);

    this.formFields[0].form?.get('userData.firstName').disable();
    this.formFields[0].form?.get('userData.lastName').disable();
    this.formFields[0].form?.get('userData.email').disable();

    this.isUserSelected = true;
  }

  onRolesSelectionChange(result: { validFrom: Date; validUntil: Date }) {
    this.selectedRolesValidFrom = result.validFrom;
    this.selectedRolesValidUntil = result.validUntil;
  }

  onDelete() {
    this.formFields[0].form?.get('userData.firstName')?.setValue(null);
    this.formFields[0].form?.get('userData.lastName')?.setValue(null);
    this.formFields[0].form?.get('userData.email')?.setValue(null);
    this.formFields[0].form
      ?.get('userData.organisation.searchResult')
      ?.setValue(null);

    if (!this.isTelekomChecked) {
      this.formFields[0].form?.get('userData.firstName').enable();
      this.formFields[0].form?.get('userData.lastName').enable();
      this.formFields[0].form?.get('userData.email').enable();
      this.formFields[0].form?.get('userData.roles').enable();
    }

    this.isUserSelected = false;
  }

  private toDateOnlyString(
    value: Date | string | undefined
  ): string | undefined {
    return value ? moment(value).format('YYYY-MM-DD') : undefined;
  }

  /**
   * Gueltigkeitszeitraum je Rolle. Jede Rolle traegt den Zeitraum, der beim
   * Hinzufuegen im Dialog gewaehlt wurde.
   */
  private toRoleValidities(roles: RoleViewModel[]): RoleValidityRequest[] {
    return (roles ?? []).map(role => ({
      roleId: role.id,
      validFrom: this.toDateOnlyString(role.validFrom) as unknown as Date,
      validUntil: this.toDateOnlyString(role.validUntil) as unknown as Date,
    }));
  }

  createUserRequest() {
    this.loading = true;
    let request: UserAdministrationIncidentRequest;

    if (this.isUserSelected) {
      request = {
        firstName:
          this.form.value['contact']?.searchResult?.firstName ??
          this.form.value['employee']?.searchResult?.firstName ??
          null,
        lastName:
          this.form.value['contact']?.searchResult?.lastName ??
          this.form.value['employee']?.searchResult?.lastName ??
          null,
        email:
          this.form.value['contact']?.searchResult?.email ??
          this.form.value['employee']?.searchResult.email ??
          null,
        companyId:
          this.form.value['contact']?.searchResult?.company?.id ?? null,
        b2cUserId: this.form.value['employee']?.searchResult?.id ?? null,
        contactId: this.form.value['contact']?.searchResult?.contactId ?? null,
        roles:
          this.form.value['userData']?.roles?.map(
            (role: RoleViewModel) => role.id
          ) ?? null,
        roleValidities: this.toRoleValidities(
          this.form.value['userData']?.roles
        ),
        validFrom: this.toDateOnlyString(
          this.selectedRolesValidFrom
        ) as unknown as Date,
        validUntil: this.toDateOnlyString(
          this.selectedRolesValidUntil
        ) as unknown as Date,
      };
    } else {
      request = {
        firstName: this.form.value['userData']?.firstName ?? null,
        lastName: this.form.value['userData']?.lastName ?? null,
        email: this.form.value['userData']?.email ?? null,
        companyId:
          this.form.value['userData']?.organisation?.searchResult?.id ?? null,
        b2cUserId: null,
        contactId: null,
        roles:
          this.form.value['userData']?.roles?.map(
            (role: RoleViewModel) => role.id
          ) ?? null,
        roleValidities: this.toRoleValidities(
          this.form.value['userData']?.roles
        ),
        validFrom: this.toDateOnlyString(
          this.selectedRolesValidFrom
        ) as unknown as Date,
        validUntil: this.toDateOnlyString(
          this.selectedRolesValidUntil
        ) as unknown as Date,
      };
    }

    this.userAdministrationClient.createUser(request).subscribe({
      next: () => {
        this.loading = false;
        this.openSuccessDialog();
      },
      error: err => {
        this.loading = false;
        const errorTitle = this.translateService.instant(
          'ticket.create.error.title'
        );
        let errorMessage = this.translateService.instant(
          'generic.errorMessage'
        );
        if (err.status === 409) {
          errorMessage = this.translateService.instant(
            'administration.create_user.error_message_user_exists'
          );
        }
        this.openErrorDialog(errorTitle, errorMessage);
      },
    });
  }

  override getStatusPath() {
    return '/dashboard';
  }
}
